// js/custom-element/ui/rid-native-video.js
var RidNativeVideo = class extends HTMLElement {
  constructor() {
    super();
    this.hasLoaded = false;
    if (this.autoPlay) {
      // Never started, and never injected: pausing rejects the play promise into
      // _handleAutoplayFailure, and injecting costs metadata plus a 100px poster for nothing.
      if (this.querySelector("[data-rid-preview-toggle]") && window.MediaFeatures && window.MediaFeatures.prefersReducedMotion()) {
        this._revealPoster();
      } else {
        this.play();
      }
    } else {
      this.addEventListener("click", this._playFromUser.bind(this), { once: true });
    }
  }
  get autoPlay() {
    return this.hasAttribute("autoplay");
  }
  _playFromUser() {
    this._userActivated = true;
    this.play();
  }
  play() {
    if (!this.hasLoaded) {
      this._replaceContent();
    }
    this.querySelector("video")
      .play()
      .then(() => {
        var poster = this.querySelector(".video-wrapper__poster");
        if (poster) {
          poster.style.display = "none";
        }
        this._showPauseControl(true);
        // Outside the poster branch: the click path wipes the poster, so gating on it mutes this.
        if (this._userActivated) {
          this._announceState("Video is now playing");
        }
      })
      .catch((error) => {
        console.warn("Autoplay blocked, falling back", error);
        this._handleAutoplayFailure();
      });
  }
  pause() {
    if (this.hasLoaded) {
      this.querySelector("video").pause();
    }
  }
  _handleAutoplayFailure() {
    if (this._showHideEnabledDisabledAutoplaySections()) {
      return;
    }
    // Or the browser retries autoplay behind the poster revealed below.
    this.querySelector("video")?.removeAttribute("autoplay");
    this._revealPoster();
    // For hosts whose start control lives outside them, e.g. a slideshow slide's play button.
    this.dispatchEvent(new CustomEvent("video:autoplay-failed", { bubbles: true }));
  }

  // No focus() in here: both callers run at upgrade time with no user action behind them.
  _revealPoster() {
    var poster = this.querySelector(".video-wrapper__poster");
    // Ahead of the pause control: autoplay-off hosts wiped the poster while a teaser loop runs.
    if (!poster) {
      return;
    }
    poster.style.display = "";
    this._showPauseControl(false);
    var playButton = poster.querySelector(".video-wrapper__play-button");
    (playButton || poster).addEventListener("click", this._playFromUser.bind(this), { once: true });
  }

  // Hidden while nothing plays: the name is fixed at "Pause video", so neither aria-pressed
  // value is true. style.display, not [hidden]: rid-theme.css sets display: flex on it.
  _showPauseControl(isVisible) {
    var toggle = this.querySelector("[data-rid-preview-toggle]");
    if (!toggle) {
      return;
    }
    toggle.style.display = isVisible ? "" : "none";
    if (isVisible) {
      ridSetToggleState(toggle, false);
    }
  }
  // An aria-hidden subtree is not exposed to AT, so the live region would be dead markup.
  _announceState(message) {
    if (this.closest('[aria-hidden="true"]')) {
      return;
    }
    var el = this.querySelector(".video-wrapper__sr-status");
    if (!el) {
      el = document.createElement("span");
      el.className = "video-wrapper__sr-status visually-hidden";
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      this.appendChild(el);
    }
    el.textContent = message;
  }
  // Returns whether the swap landed, so the caller can fall through to the poster path.
  _showHideEnabledDisabledAutoplaySections() {
    var isMobile = window.innerWidth <= 740;
    var sectionId = this.getAttribute("data-section-id");
    if (!sectionId) return false;

    var findSections = function(pattern) {
      // An empty pattern must not emit a second dash: single-breakpoint sections
      // (rid-video-banner) render id="...-section-<id>", not "...-section--<id>".
      var suffix = (pattern ? pattern + "-" : "") + sectionId;
      return {
        enabled: document.getElementById("video-banner-autoplay-enabled-section-" + suffix),
        disabled: document.getElementById("video-banner-autoplay-disabled-section-" + suffix),
      };
    };

    var sections = findSections(isMobile ? "mobile" : "desktop");
    if (!sections.enabled || !sections.disabled) {
      sections = findSections("");
    }

    if (!sections.enabled || !sections.disabled) {
      return false;
    }

    sections.enabled.style.display = "none";
    sections.disabled.style.display = "block";
    return true;
  }

  _replaceContent() {
    var node = this.querySelector("template");
    if (!node) {
      return;
    }
    node = node.content.firstElementChild.cloneNode(true);
    var didWipe = !this.hasAttribute("autoplay");
    if (didWipe) {
      this.innerHTML = "";
    }
    this.appendChild(node);
    node.addEventListener("play", () => {
      this.dispatchEvent(new CustomEvent("video:played", { bubbles: true }));
    });
    node.addEventListener("pause", () => {
      this.dispatchEvent(new CustomEvent("video:paused", { bubbles: true }));
    });
    this.hasLoaded = true;
    // Focus only after a wipe: it took the activated button, dropping focus to <body>. Seeds the
    // live region first so it exists before text lands. Never focus into an aria-hidden subtree.
    if (didWipe && this._userActivated && !this.closest('[aria-hidden="true"]')) {
      this._announceState("");
      if (!node.hasAttribute("controls")) {
        node.setAttribute("tabindex", "-1");
      }
      node.focus();
    }
  }
};
window.customElements.define("rid-native-video", RidNativeVideo);

// js/custom-element/ui/rid-order-now.js
var RidOrderNow = class extends HTMLElement {
  constructor() {
    super();
    this.preorderDate = this.getAttribute("data-pre-order-date");
    this.orderNowTextData = document.getElementById("order-now-text-data");
    this.presetText = this.orderNowTextData?.getAttribute(
      "data-order-now-text"
    );
    this.init();
  }

  async init() {
    // Find both text elements - we need to handle both regular and mini-cart versions
    this.textElements = [
      this.querySelector("#order-now-snippet-text"),
      this.querySelector("#order-now-mini-cart-snippet-text"),
    ].filter(Boolean); // Remove any null elements

    if (this.textElements.length === 0) {
      console.error("No order now text elements found");
      return;
    }

    if (this.preorderDate) {
      this.handlePreorderDate();
    } else if (this.presetText) {
      this.handlePresetText();
    } else {
      await this.handleDynamicShipping();
    }
  }

  handlePreorderDate() {
    const [year, month, day] = this.preorderDate.split("-");
    const dateObj = new Date(year, month - 1, day);

    // Any preorder ships the whole order, so an unusable date leaves the widget hidden rather
    // than falling through to a fast-ship estimate the order cannot meet.
    if (Number.isNaN(dateObj.getTime())) {
      console.error("Unparseable preorder date:", this.preorderDate);
      return;
    }

    const options = { year: "numeric", month: "long", day: "numeric" };
    const formattedDate = dateObj.toLocaleDateString("en-US", options);

    const text = `Order <b>NOW</b> and items will ship on or before <b>${formattedDate}</b>.`;
    this.updateAllTextElements(text);

    const fastMessage = this.querySelector("#get-it-fast-message");
    if (fastMessage) {
      fastMessage.style.display = "none";
    }

    // The promo slot advertises fast shipping, which no preorder order qualifies for.
    const additionalText = this.querySelector("#additional-order-now-text");
    if (additionalText) {
      additionalText.style.display = "none";
    }

    this.reveal();
  }

  handlePresetText() {
    this.updateAllTextElements(this.presetText);
    this.reveal();
  }

  // Opacity alone leaves the action buttons focusable and announced, so hiding rides on
  // visibility and every early return keeps them out of the tab order.
  reveal() {
    this.style.visibility = "visible";
    this.style.opacity = "1";
  }

  async handleDynamicShipping() {
    try {
      const response = await fetch(
        "https://roadid.com/pages/shipping_date_v2.json"
      );
      const responseJson = await response.json();
      const data = JSON.parse(responseJson.page.body_html.replace(/'/g, '"'));

      const now = new Date();
      const cutoffDateTime = new Date(data.cutoffDateTime);
      let shipsWhen;
      let expectedShipDate;

      if (now < cutoffDateTime) {
        expectedShipDate = new Date(data.preCutoffShipDate);
      } else {
        expectedShipDate = new Date(data.postCutoffShipDate);
      }

      const normalizeDate = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
      };

      const normalizedExpectedShipDate = normalizeDate(expectedShipDate);
      const normalizedNow = normalizeDate(now);
      const dayDifference = Math.ceil(
        (normalizedExpectedShipDate - normalizedNow) / (1000 * 60 * 60 * 24)
      );

      if (dayDifference === 0) {
        shipsWhen = "today";
      } else if (dayDifference === 1) {
        shipsWhen = "tomorrow";
      } else if (dayDifference > 6) {
        shipsWhen = `in ${dayDifference} days`;
      } else {
        shipsWhen = expectedShipDate.toLocaleString("en-US", {
          weekday: "long",
        });
      }

      const timeDifferenceMs = cutoffDateTime - now;
      const remainingHours = Math.floor(timeDifferenceMs / (1000 * 60 * 60));
      const remainingMinutes = Math.floor(
        (timeDifferenceMs % (1000 * 60 * 60)) / (1000 * 60)
      );
      const isDeadlineApproaching = remainingHours < 6;

      let text =
        isDeadlineApproaching === false
          ? `Order <b>now</b> and it ships <b>${shipsWhen}!</b>`
          : remainingHours > 0 || remainingMinutes > 0
          ? `Order within <b>${
              remainingHours > 0
                ? `${remainingHours} ${remainingHours === 1 ? "hour" : "hours"}`
                : ""
            } ${
              remainingMinutes > 0
                ? `${remainingMinutes} ${
                    remainingMinutes === 1 ? "minute" : "minutes"
                  }`
                : ""
            }</b> and it ships <b>${shipsWhen}!</b>`
          : `Order now and it ships <b>${shipsWhen}!</b>`;

      if (this.orderNowTextData) {
        this.orderNowTextData.setAttribute("data-order-now-text", text);
      }

      this.updateAllTextElements(text);
      this.reveal();
    } catch (e) {
      console.error("Error fetching shipping information:", e);
    }
  }

  updateAllTextElements(text) {
    this.textElements.forEach((element) => {
      element.innerHTML = text;
    });
  }
};

window.customElements.define("rid-order-now", RidOrderNow);

/* CART FREE PRODUCT SYNC */

(function () {
  'use strict';

  function getConfig() {
    return window.rackRabbitCartConfig || null;
  }

  // Don't re-warn about the same missing variantId on every cart:updated
  var warnedMissingVariant = {};
  var warnedBadPreorderDate = {};

  // GID format in config vs numeric in data-product-id
  function numericId(gid) {
    if (!gid) return null;
    var parts = String(gid).split('/');
    return parts[parts.length - 1];
  }

  // TH 09/10/26 - Preorder date for a free product, from the same config rule as variantId.
  // ISO YYYY-MM-DD only (a full ISO timestamp is trimmed to the date) so the cart's date math and
  // the PDP producer stay on one format. Anything else is skipped, not guessed at.
  function isoPreorderDate(value) {
    if (!value) return null;
    var str = String(value).trim();
    if (!/^\d{4}-\d{2}-\d{2}/.test(str)) {
      if (!warnedBadPreorderDate[str]) {
        console.warn('Free product preorder date is not ISO YYYY-MM-DD; not stamping _preorder-date:', str);
        warnedBadPreorderDate[str] = true;
      }
      return null;
    }
    return str.slice(0, 10);
  }

  // Returns allowed qty for a free product given current hero qty, per config rules
  function getFreeQty(productId, heroQty, config) {
    if (!config || !config.freeProducts) return null;
    var gidKey = 'gid://shopify/Product/' + productId;
    var rule = config.freeProducts[gidKey];
    if (!rule) return null;

    if (rule.rule === '1:1') {
      return heroQty;
    } else if (rule.rule === 'n-1') {
      var minHeroQty = rule.minHeroQty || 2;
      return heroQty >= minHeroQty ? heroQty - 1 : 0;
    }
    return null;
  }

  // TH 06/02/26 - Walk the config, not the cart. Old version only touched free lines already
  // in the cart, so an n-1 freebie (minHeroQty 2) added at hero qty 1 had no line to bump when
  // the qty went up later. Existing line -> update, none yet -> add.
  const syncFreeProductQtys = async function (cart) {
    var config = getConfig();
    if (!config || !config.heroProductId || !config.freeProducts) return;

    var heroNumericId = numericId(config.heroProductId);
    if (!heroNumericId) return;

    // Reuse the cart Focal already fetched (event.detail.cart); only hit the network if it's missing or junk
    if (!cart || !Array.isArray(cart.items)) {
      try {
        var cartResponse = await fetch('/cart.js');
        if (!cartResponse.ok) return;
        cart = await cartResponse.json();
      } catch (err) {
        console.error('Free product qty sync failed:', err);
        return;
      }
    }

    // Sum non-free hero lines (hero product can be split across multiple lines)
    var heroQty = 0;
    cart.items.forEach(function (item) {
      if (String(item.product_id) === heroNumericId) {
        var isFree = item.properties && item.properties._rack_rabbit_free === 'true';
        if (!isFree) heroQty += item.quantity;
      }
    });

    var updates = {};
    var needsUpdate = false;
    var adds = [];

    // Keyed by product GID
    Object.keys(config.freeProducts).forEach(function (gidKey) {
      var productNumericId = numericId(gidKey);
      if (!productNumericId) return;

      var allowedQty = getFreeQty(productNumericId, heroQty, config);
      if (allowedQty === null) return;

      // Free line for this product, if it's already in the cart
      var existing = null;
      cart.items.forEach(function (item) {
        if (existing) return;
        var isFree = item.properties && item.properties._rack_rabbit_free === 'true';
        if (isFree && String(item.product_id) === productNumericId) {
          existing = item;
        }
      });

      if (existing) {
        // Correct the qty if it's off; 0 drops the line
        if (existing.quantity !== allowedQty) {
          updates[existing.key] = allowedQty;
          needsUpdate = true;
        }
        return;
      }

      if (allowedQty <= 0) return;

      // Earned but not in the cart yet -- add it
      var rule = config.freeProducts[gidKey];
      var variantNumericId = numericId(rule && rule.variantId);
      if (!variantNumericId) {
        // No variantId in the metafield yet, so we can't add it. Warn once and keep going --
        // the rest still syncs, and this can ship before the metafield's updated.
        if (!warnedMissingVariant[gidKey]) {
          console.warn('Free product missing variantId in config; cannot auto-add. Product:', gidKey);
          warnedMissingVariant[gidKey] = true;
        }
        return;
      }

      // TH 09/10/26 - The cart states one ship date: the MAX _preorder-date across line items. An
      // unstamped free line adds nothing to that max, so a late-shipping freebie would make the
      // notice understate the date. Stamp when the config has one; absence tolerated like variantId.
      var addProperties = { _rack_rabbit_free: 'true' };
      var freePreorderDate = isoPreorderDate(rule && rule.preorderDate);
      if (freePreorderDate) {
        addProperties['_preorder-date'] = freePreorderDate;
      }

      adds.push({
        id: parseInt(variantNumericId, 10),
        quantity: allowedQty,
        // Cart Transform zeroes the price off _rack_rabbit_free; the extra property doesn't affect it
        properties: addProperties
      });
    });

    if (!needsUpdate && adds.length === 0) return;

    try {
      // A failed add shouldn't skip the updates -- they're different lines. Track whether anything
      // actually changed so we don't refresh for nothing.
      var didChange = false;

      if (adds.length > 0) {
        var addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: adds })
        });
        if (addResponse.ok) {
          didChange = true;
        } else {
          console.error('Free product qty sync failed (add):', addResponse.status);
        }
      }

      if (needsUpdate) {
        var updateResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ updates: updates })
        });
        if (updateResponse.ok) {
          didChange = true;
        } else {
          console.error('Free product qty sync failed (update):', updateResponse.status);
        }
      }

      if (!didChange) return;

      // Focal has no re-render path for the cart table; reload handles it
      if (window.location.pathname === '/cart') {
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));
    } catch (err) {
      console.error('Free product qty sync failed:', err);
    }
  };

  // Guard against overlapping runs. Running the add twice doesn't no-op -- it adds the free line a
  // second time and doubles it (/cart/add.js merges by variant + props). Two runs racing both see
  // "no free line" and both add. A double-click on qty "+" is enough to fire cart:updated twice.
  // Drop the second run; the next one re-syncs. _rackSyncCart below stays unguarded -- the /cart
  // path is serial (reload navigates away).
  var syncing = false;
  document.addEventListener('cart:updated', function (event) {
    if (syncing) return;
    syncing = true;
    syncFreeProductQtys(event.detail && event.detail.cart).finally(function () {
      syncing = false;
    });
  });

  // Exposed for theme.js to call before the /cart page reload (cart:updated never fires there)
  window._rackSyncCart = syncFreeProductQtys;

  // Handle "increase" on free items — adds a paid line for the overflow unit
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.js-rack-free-increase');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    var variantId = btn.dataset.variantId;
    if (!variantId) return;

    // Trigger line-item spinner
    var lineItemEl = btn.closest('line-item, .line-item');
    if (lineItemEl) {
      lineItemEl.dispatchEvent(new CustomEvent('line-item-quantity:change:start', { bubbles: true }));
    }

    btn.setAttribute('aria-busy', 'true');

    // Add 1 of the same variant WITHOUT _rack_rabbit_free — creates a separate paid line.
    // Non-free line item properties are pre-rendered in data-line-item-properties by Liquid.
    var lineItem = { id: parseInt(variantId, 10), quantity: 1 };
    if (btn.dataset.lineItemProperties) {
      try {
        lineItem.properties = JSON.parse(btn.dataset.lineItemProperties);
      } catch (e) {
        console.warn('Failed to parse line item properties:', btn.dataset.lineItemProperties, e);
      }
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [lineItem] })
    })
      .then(function () {
        return fetch('/cart.js');
      })
      .then(function (response) { return response.json(); })
      .then(function (cartContent) {
        if (window.location.pathname === '/cart') {
          window.location.reload();
          return;
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { cart: cartContent }
        }));
      })
      .catch(function (err) {
        console.error('Failed to add paid overflow item:', err);
      })
      .finally(function () {
        btn.removeAttribute('aria-busy');
        if (lineItemEl) {
          lineItemEl.dispatchEvent(new CustomEvent('line-item-quantity:change:end', { bubbles: true }));
        }
      });
  });
})();

// aria-pressed is the sole carrier of state; the name stays fixed or the two contradict each
// other. The glyph swap keys off aria-pressed in rid-theme.css.
function ridSetToggleState(toggle, isPaused) {
  toggle.setAttribute("aria-pressed", isPaused ? "true" : "false");
}

// js/custom-element/section/rid-slideshow.js
// Slides keep the slide-show-item tag: Focal's connectedCallback queries it by name.
// Guarded on the base class; `class extends undefined` would throw at parse time.
var FocalSlideshow = window.customElements.get("slide-show");

var RidSlideshow = FocalSlideshow && class extends FocalSlideshow {
  connectedCallback() {
    super.connectedCallback();

    this.userPaused = false;
    // Both breakpoints' videos are in the DOM; re-check which is visible on crossing.
    this.breakpointQuery = window.matchMedia("(min-width: 1000px)");
    this.boundBreakpointChange = this.handleBreakpointChange.bind(this);
    this.breakpointQuery.addEventListener("change", this.boundBreakpointChange);
    this.addEventListener("video:played", this.handleVideoPlayed.bind(this));
    this.addEventListener("video:autoplay-failed", this.handleAutoplayFailed.bind(this));

    this.delegate.on("click", "[data-rid-slideshow-toggle]", this.handleToggleClick.bind(this));
    this.delegate.on("click", "[data-rid-slide-play]", this.handleSlidePlayClick.bind(this));

    this._activateWhenVisible();
  }
  // Theme editor re-renders sections on every setting change; don't leak per edit.
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
    if (this.breakpointQuery && this.boundBreakpointChange) {
      this.breakpointQuery.removeEventListener("change", this.boundBreakpointChange);
    }
    this._pauseAllVideos();
  }

  handleBreakpointChange() {
    this._playVideos(this.items[this.selectedIndex]);
  }

  // is-playing is a deliberate one-way latch, not a leak: only .is-playing video is opacity 1, so
  // clearing it in _pauseVideos would blank a played slide until the next video:played, its poster
  // being hidden by then.
  handleVideoPlayed(event) {
    if (event.target && event.target.classList) {
      event.target.classList.add("is-playing");
    }
  }
  // Both the poster and the start control sit outside the host, because _replaceContent wipes
  // innerHTML on a host with no autoplay attribute. So neither is reachable by _revealPoster.
  handleAutoplayFailed(event) {
    const item = event.target.closest("slide-show-item");
    if (!item) {
      return;
    }
    const poster = event.target.parentElement?.querySelector(".rid-slideshow__poster");
    if (poster) {
      poster.hidden = false;
    }
    this._restorePlayButton(item, false);
  }
  handleToggleClick(event, target) {
    event.preventDefault();
    this.userPaused = !this.userPaused;
    ridSetToggleState(target, this.userPaused);

    if (this.userPaused) {
      this.pausePlayer();
      this._pauseAllVideos();
    } else {
      // startPlayer() is a no-op without the auto-play attribute, which the section omits when
      // rotation is off, so this cannot start a rotation the merchant disabled. No extra guard.
      this.startPlayer();
      this._playVideos(this.items[this.selectedIndex]);
    }
  }
  // Touches this slide's video only. Rotation state and the section toggle belong to the
  // slideshow, so starting a video must not move either.
  handleSlidePlayClick(event, target) {
    event.preventDefault();
    const item = target.closest("slide-show-item");
    if (!item) {
      return;
    }

    // Marks this playback as user-requested so the toggle's resume branch does not pause it.
    this._videosIn(item).filter(this._isVisible.bind(this)).forEach(function (video) {
      video.dataset.ridManualPlay = "true";
      video.play();
    });

    target.hidden = true;
  }
  // Not Focal's untilVisible(): it shares this.intersectionObserver, which the
  // shopify:block:select handler disconnects, stranding the promise.
  _activateWhenVisible() {
    if (!window.IntersectionObserver) {
      this._playVideos(this.items[this.selectedIndex]);
      return;
    }

    this.videoObserver = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) {
        this.videoObserver.disconnect();
        this._playVideos(this.items[this.selectedIndex]);
      }
    }.bind(this), { rootMargin: "200px" });

    this.videoObserver.observe(this);
  }

  async select(index, shouldTransition = true, reverseDirection = false) {
    // Mirror Focal's bail-out first: a call it no-ops must not claim the generation.
    if (this.transitionType === "reveal" && this.isTransitioning) {
      return super.select(index, shouldTransition, reverseDirection);
    }

    const generation = (this.selectGeneration || 0) + 1;
    this.selectGeneration = generation;

    this._pauseVideos(this.items[this.selectedIndex]);

    await super.select(index, shouldTransition, reverseDirection);

    if (generation !== this.selectGeneration) {
      return;
    }
    this._playVideos(this.items[this.selectedIndex]);
  }

  _videosIn(item) {
    return item ? Array.from(item.querySelectorAll("rid-native-video")) : [];
  }
  _isVisible(video) {
    return !!video.parentElement && getComputedStyle(video.parentElement).display !== "none";
  }
  _playableVideos(item) {
    if (this.userPaused) {
      return [];
    }

    const autoStartAllowed = !window.MediaFeatures.prefersReducedMotion();

    return this._videosIn(item).filter(function (video) {
      if (!this._isVisible(video)) {
        return false;
      }
      // User-initiated, so neither reduced motion nor video_autoplay: false may stop it.
      if (video.dataset.ridManualPlay === "true") {
        return true;
      }
      return autoStartAllowed && video.getAttribute("data-video-autoplay") !== "false";
    }.bind(this));
  }

  _playVideos(item) {
    const playable = this._playableVideos(item);

    this._videosIn(item).forEach(function (video) {
      if (playable.indexOf(video) === -1) {
        video.pause();
      } else {
        video.play();
      }
    });

    // Reduced motion (and autoplay off) declines before play() is ever called, so no promise
    // rejects and video:autoplay-failed cannot reveal the poster. Do it for the declined hosts.
    if (playable.length === 0) {
      this._videosIn(item).forEach(function (video) {
        const poster = video.parentElement?.querySelector(".rid-slideshow__poster");
        if (poster) {
          poster.hidden = false;
        }
      });
    }

    // Or an autoplay-off slide stays paused with its only control hidden and no way back.
    this._restorePlayButton(item, playable.length > 0);
  }

  _restorePlayButton(item, isPlaying) {
    if (!item) {
      return;
    }
    const button = item.querySelector("[data-rid-slide-play]");
    if (button) {
      button.hidden = isPlaying;
    }
  }

  // Leaving the slide ends the manual play so video_autoplay: false applies again on return.
  // The toggle's pause branch keeps the mark deliberately.
  _pauseVideos(item) {
    const videos = this._videosIn(item);
    videos.forEach(function (video) {
      video.pause();
      delete video.dataset.ridManualPlay;
    });
    // Only a slide that never autoplays needs its control back. Restoring unconditionally made
    // an autoplay-on slide flash a play button every time rotation left it.
    const needsControl = videos.some(function (video) {
      return video.getAttribute("data-video-autoplay") === "false";
    });
    this._restorePlayButton(item, needsControl === false);
  }
  // A video can outlive its slide if a transition was interrupted.
  _pauseAllVideos() {
    Array.from(this.querySelectorAll("rid-native-video")).forEach(function (video) {
      video.pause();
    });
  }
};

if (RidSlideshow) {
  window.customElements.define("rid-slideshow", RidSlideshow);
} else {
  console.error("rid-slideshow: slide-show base class missing, video slides disabled");
}

// js/custom-element/section/rid-multi-row-media.js
// Not window.MediaFeatures alone: a miss there would read as "motion is fine", which is the
// unsafe direction for a gate that decides whether to autoplay.
function ridPrefersReducedMotion() {
  if (window.MediaFeatures) {
    return window.MediaFeatures.prefersReducedMotion();
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Autoplay is a markup attribute and the browser owns scheduling, including offscreen rows.
var RidMultiRowMedia = class extends HTMLElement {
  connectedCallback() {
    if (this.hasSetUp) {
      return;
    }
    this.hasSetUp = true;
    this._setUp();
  }

  _setUp() {
    this.video = this.querySelector("video");
    if (!this.video) {
      return;
    }

    // Native controls are the JS-off pause mechanism. Both our own controls ship hidden for the
    // same reason, so nothing dead is exposed when this element never upgrades.
    this.video.removeAttribute("controls");

    this.playButton = this.querySelector("[data-rid-multi-row-play]");
    this.toggle = this.querySelector("[data-rid-multi-row-toggle]");
    this.startedFromUser = false;

    // video_tag emits the fallback img with no alt and derives aria-label from the Shopify media
    // alt, which the block setting has to be able to override.
    var alt = this.dataset.videoAlt;
    if (alt) {
      var fallbackImage = this.video.querySelector("img");
      if (fallbackImage) {
        fallbackImage.alt = alt;
      }
      this.video.setAttribute("aria-label", alt);
    }

    this.video.addEventListener("play", this._handlePlay.bind(this));
    this.video.addEventListener("pause", this._handlePause.bind(this));
    // suspend, not canplaythrough: a refused autoplay honouring preload="metadata" stalls at
    // HAVE_METADATA and never fires canplaythrough. suspend is the only signal that path emits.
    this.video.addEventListener("suspend", this._syncRefusedState.bind(this), { once: true });
    this.video.addEventListener("canplaythrough", this._syncRefusedState.bind(this), { once: true });

    if (this.playButton) {
      this.playButton.addEventListener("click", this._startFromUser.bind(this));
    }
    if (this.toggle) {
      this.toggle.addEventListener("click", this._handleToggle.bind(this));
    }

    if (this.video.autoplay && ridPrefersReducedMotion()) {
      this.video.removeAttribute("autoplay");
      this.video.pause();
    }

    if (this.video.autoplay && this.toggle) {
      this.toggle.hidden = false;
    } else if (this.playButton) {
      this.playButton.hidden = false;
    }

    if (this.video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      this._syncRefusedState();
    }
  }

  _syncRefusedState() {
    if (this.hasPlayed || !this.toggle || !this.video.autoplay || !this.video.paused) {
      return;
    }
    this.toggle.hidden = false;
    ridSetToggleState(this.toggle, true);
  }

  // Live paused state, not aria-pressed: a refused autoplay must still start on the first click.
  _handleToggle() {
    if (this.video.paused) {
      this.video.play().catch(function () {});
    } else {
      this.video.pause();
    }
  }

  _startFromUser() {
    this.startedFromUser = true;
    var promise = this.video.play();
    if (promise) {
      promise.catch(function () {
        this.startedFromUser = false;
      }.bind(this));
    }
  }

  _handlePlay() {
    this.hasPlayed = true;
    if (this.playButton) {
      this.playButton.hidden = true;
    }
    if (this.toggle) {
      this.toggle.hidden = false;
      ridSetToggleState(this.toggle, false);
      if (this.startedFromUser) {
        this.startedFromUser = false;
        this.toggle.focus();
      }
    }
  }

  _handlePause() {
    if (this.toggle) {
      ridSetToggleState(this.toggle, true);
    }
  }
};
window.customElements.define("rid-multi-row-media", RidMultiRowMedia);

// js/section/rid-product-grid.js
// Keeps the selected thumbnail in view. native-carousel moves page-dots' aria-current without an
// event, and page-dots' align-selected centers on the window, not the strip, so follow the slide
// scroll instead. Delegated on document: product-rerender replaces the cards on every variant change.
function ridKeepThumbnailInView(slides) {
  const index = Math.round(Math.abs(slides.scrollLeft) / slides.clientWidth);
  if (slides._ridThumbnailIndex === index) return;
  slides._ridThumbnailIndex = index;

  const strip = slides.parentElement.querySelector(".rid-product-grid__thumbs");
  const thumbnail = strip && strip.children[index];
  if (!thumbnail) return;

  const stripRect = strip.getBoundingClientRect();
  const thumbnailRect = thumbnail.getBoundingClientRect();

  if (thumbnailRect.left < stripRect.left) {
    strip.scrollBy({ left: thumbnailRect.left - stripRect.left, behavior: "smooth" });
  } else if (thumbnailRect.right > stripRect.right) {
    strip.scrollBy({ left: thumbnailRect.right - stripRect.right, behavior: "smooth" });
  }
}

let ridThumbnailFrame = 0;
document.addEventListener("scroll", (event) => {
  const slides = event.target;
  if (ridThumbnailFrame || !slides.classList || !slides.classList.contains("rid-product-grid__slides")) return;

  ridThumbnailFrame = requestAnimationFrame(() => {
    ridThumbnailFrame = 0;
    ridKeepThumbnailInView(slides);
  });
}, { capture: true, passive: true });
